import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList } from '../types/navigation.types';
import { SCREEN_NAMES } from '../config/constants';
import { getStackScreenOptions } from '../config/screenOptions';
import useTheme from '../../hooks/useTheme';

// Lazy import screens for better performance
const RequisitionListScreen = React.lazy(() => import('../../screens/enterprise/requisition/requisition-list-screen'));
const RequisitionFormScreen = React.lazy(() => import('../../screens/enterprise/requisition/requisition-form-screen'));
const RequisitionDetailScreen = React.lazy(() => import('../../screens/enterprise/requisition/requisition-detail-screen'));
const ApprovalDashboardScreen = React.lazy(() => import('../../screens/enterprise/requisition/approval-dashboard-screen'));

const BudgetDashboardScreen = React.lazy(() => import('../../screens/enterprise/budget/budget-dashboard-screen'));
const BudgetDetailScreen = React.lazy(() => import('../../screens/enterprise/budget/budget-detail-screen'));
const BudgetFormScreen = React.lazy(() => import('../../screens/enterprise/budget/budget-form-screen'));
const BudgetVarianceScreen = React.lazy(() => import('../../screens/enterprise/budget/budget-variance-screen'));

const QualityInspectionScreen = React.lazy(() => import('../../screens/enterprise/quality-control/quality-inspection-screen'));
const DamageAssessmentScreen = React.lazy(() => import('../../screens/enterprise/quality-control/damage-assessment-screen'));
const RemediationDashboardScreen = React.lazy(() => import('../../screens/enterprise/quality-control/remediation-dashboard-screen'));

const InventoryAnalyticsDashboardScreen = React.lazy(() => import('../../screens/enterprise/inventory/inventory-analytics-dashboard-screen'));

const GoodsReceiptScreen = React.lazy(() => import('../../screens/enterprise/goods-receipt/goods-receipt-screen'));
const PartialReceiptScreen = React.lazy(() => import('../../screens/enterprise/goods-receipt/partial-receipt-screen'));
const GoodsReceiptResultScreen = React.lazy(() => import('../../screens/enterprise/goods-receipt/goods-receipt-result-screen'));

const Stack = createStackNavigator<RootStackParamList>();

export function EnterpriseStack() {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <Stack.Navigator
      screenOptions={getStackScreenOptions(colors)}
      initialRouteName="ApprovalDashboard"
    >
      {/* Requisition Management */}
      <Stack.Screen
        name="RequisitionList"
        component={RequisitionListScreen}
        options={{
          title: t('requisition.list.title', 'Purchase Requisitions'),
          headerShown: true,
        }}
      />
      <Stack.Screen
        name="RequisitionForm"
        component={RequisitionFormScreen}
        options={{
          title: t('requisition.form.title', 'New Requisition'),
          headerShown: false, // Custom header in component
        }}
      />
      <Stack.Screen
        name="RequisitionDetail"
        component={RequisitionDetailScreen}
        options={{
          title: t('requisition.detail.title', 'Requisition Details'),
          headerShown: true,
        }}
      />
      <Stack.Screen
        name="ApprovalDashboard"
        component={ApprovalDashboardScreen}
        options={{
          title: t('approval.dashboard.title', 'Approvals'),
          headerShown: true,
        }}
      />

      {/* Budget Management */}
      <Stack.Screen
        name="BudgetDashboard"
        component={BudgetDashboardScreen}
        options={{
          title: t('budget.dashboard.title', 'Budget Management'),
          headerShown: false, // Custom header in component
        }}
      />
      <Stack.Screen
        name="BudgetDetail"
        component={BudgetDetailScreen}
        options={{
          title: t('budget.detail.title', 'Budget Details'),
          headerShown: true,
        }}
      />
      <Stack.Screen
        name="BudgetForm"
        component={BudgetFormScreen}
        options={{
          title: t('budget.form.title', 'Budget Form'),
          headerShown: true,
        }}
      />
      <Stack.Screen
        name="BudgetVariance"
        component={BudgetVarianceScreen}
        options={{
          title: t('budget.variance.title', 'Budget Variance'),
          headerShown: true,
        }}
      />

      {/* Quality Control */}
      <Stack.Screen
        name="QualityInspection"
        component={QualityInspectionScreen}
        options={{
          title: t('quality.inspection.title', 'Quality Inspection'),
          headerShown: false, // Custom header in component
        }}
      />
      <Stack.Screen
        name="DamageAssessment"
        component={DamageAssessmentScreen}
        options={{
          title: t('quality.damage.title', 'Damage Assessment'),
          headerShown: false, // Custom header in component
        }}
      />
      <Stack.Screen
        name="RemediationDashboard"
        component={RemediationDashboardScreen}
        options={{
          title: t('remediation.dashboard.title', 'Remediation Tracking'),
          headerShown: false, // Custom header in component
        }}
      />

      {/* Inventory Analytics */}
      <Stack.Screen
        name="InventoryAnalyticsDashboard"
        component={InventoryAnalyticsDashboardScreen}
        options={{
          title: t('inventory.analytics.title', 'Inventory Analytics'),
          headerShown: false, // Custom header in component
        }}
      />

      {/* Goods Receipt */}
      <Stack.Screen
        name="GoodsReceipt"
        component={GoodsReceiptScreen}
        options={{
          title: t('goodsReceipt.title', 'Goods Receipt'),
          headerShown: true,
        }}
      />
      <Stack.Screen
        name="PartialReceipt"
        component={PartialReceiptScreen}
        options={{
          title: t('goodsReceipt.partial.title', 'Partial Receipt'),
          headerShown: true,
        }}
      />
      <Stack.Screen
        name="GoodsReceiptResult"
        component={GoodsReceiptResultScreen}
        options={{
          title: t('goodsReceipt.result.title', 'Receipt Confirmation'),
          headerShown: true,
        }}
      />
    </Stack.Navigator>
  );
}